import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { PlusIcon, XIcon } from './icons';
import { Badge, Button, CopyButton, ErrorText, Input, cx } from './ui';
import { ApiError, errorMessage } from '../lib/api';
import { dollars, shortDate } from '../lib/format';
import type { OrgDetail, OrgInvite, OrgMember, Role, SeatLevel } from '../lib/types';
import { qk } from '../queries/keys';
import { ROLES, inviteMember, previewSeats, revokeInvite, roleDescription, roleLabel, setMemberRole, setMemberSeat, setSeatCount, type SeatQuote } from '../queries/orgs';

/**
 * The people on the team, one list: members and the invitations still out,
 * each row carrying its own state and controls. The seat count sits in the
 * header with the one way to grow it. Shared by the members page and the
 * subscription tab, since seats are people.
 *
 * The API is the authority on who may change whom: an admin trying to demote
 * an owner gets a 403 with a sentence in it, and that sentence is what we show.
 * The only rule duplicated here is hiding the controls on your own row, because
 * offering someone a button that removes themselves is just unkind.
 */

function Avatar({ name, email, avatarUrl }: { name: string | null; email: string; avatarUrl?: string | null }) {
  const initial = (name?.trim() || email).charAt(0).toUpperCase();
  return avatarUrl ? (
    <img src={avatarUrl} alt="" referrerPolicy="no-referrer" className="h-8 w-8 shrink-0 rounded-full object-cover" />
  ) : (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-caption font-medium text-chalk">
      {initial}
    </span>
  );
}

function Row({ children, muted = false }: { children: ReactNode; muted?: boolean }) {
  return (
    <li className={cx('flex flex-wrap items-center gap-3 border-b border-hairline py-3 last:border-b-0', muted && 'opacity-80')}>
      {children}
    </li>
  );
}

function RoleSelect({ value, disabled, label, onChange }: { value: Role; disabled?: boolean; label: string; onChange: (role: Role) => void }) {
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value as Role)}
      className="h-8 rounded border border-hairline bg-slate-900 px-2 text-caption text-chalk transition-colors hover:border-slate-600 focus:border-signal focus:outline-none disabled:opacity-50"
    >
      {ROLES.map((role) => (
        <option key={role} value={role}>
          {roleLabel(role)}
        </option>
      ))}
    </select>
  );
}

const SEATS: { value: SeatLevel; label: string }[] = [
  { value: 'paid', label: 'Paid seat' },
  { value: 'free', label: 'Free' },
];

function SeatSelect({ value, disabled, label, onChange }: { value: SeatLevel; disabled?: boolean; label: string; onChange: (seat: SeatLevel) => void }) {
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value as SeatLevel)}
      className="h-8 rounded border border-hairline bg-slate-900 px-2 text-caption text-chalk transition-colors hover:border-slate-600 focus:border-signal focus:outline-none disabled:opacity-50"
    >
      {SEATS.map((seat) => (
        <option key={seat.value} value={seat.value}>
          {seat.label}
        </option>
      ))}
    </select>
  );
}

function MemberRow({ orgId, member, isSelf }: { orgId: string; member: OrgMember; isSelf: boolean }) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: qk.org(orgId) });
    void client.invalidateQueries({ queryKey: qk.orgs() });
  };
  const changeRole = useMutation({
    mutationFn: (role: Role) => setMemberRole(orgId, member.user.id, role),
    onSuccess: invalidate,
  });
  const changeSeat = useMutation({
    mutationFn: (seat: SeatLevel) => setMemberSeat(orgId, member.user.id, seat),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => setMemberRole(orgId, member.user.id, null),
    onSuccess: () => {
      setConfirming(false);
      invalidate();
    },
  });
  const busy = changeRole.isPending || changeSeat.isPending || remove.isPending;
  const failure = changeRole.error ?? changeSeat.error ?? remove.error;

  return (
    <Row>
      <Avatar name={member.user.name} email={member.user.email} avatarUrl={member.user.avatarUrl} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate text-body text-chalk">
          <span className="truncate">{member.user.name ?? member.user.email}</span>
          {isSelf ? <Badge tone="neutral">You</Badge> : null}
        </p>
        <p className="truncate text-caption text-chalk-faint">
          {member.user.email} · joined {shortDate(member.createdAt)}
        </p>
      </div>
      {isSelf ? (
        <span className="text-caption text-chalk-dim">
          {roleLabel(member.role)} · {member.seat === 'paid' ? 'Paid seat' : 'Free'}
        </span>
      ) : (
        <>
          <RoleSelect value={member.role} disabled={busy} label={`Role for ${member.user.email}`} onChange={(role) => changeRole.mutate(role)} />
          {/* Owners and admins run the team, so they are always on a paid seat. */}
          {member.role === 'user' ? (
            <SeatSelect value={member.seat} disabled={busy} label={`Seat for ${member.user.email}`} onChange={(seat) => changeSeat.mutate(seat)} />
          ) : (
            <span className="text-caption text-chalk-dim">Paid seat</span>
          )}
          {confirming ? (
            <span className="flex items-center gap-1.5">
              <Button variant="danger" size="sm" busy={remove.isPending} onClick={() => remove.mutate()}>
                Remove
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={busy}
              aria-label={`Remove ${member.user.email}`}
              title="Remove from the team"
              className="flex h-8 w-8 items-center justify-center rounded text-chalk-faint hover:bg-slate-800 hover:text-chalk"
            >
              <XIcon size={14} />
            </button>
          )}
        </>
      )}
      {failure ? (
        <div className="w-full">
          <ErrorText>{errorMessage(failure)}</ErrorText>
        </div>
      ) : null}
    </Row>
  );
}

function InviteRow({ orgId, invite }: { orgId: string; invite: OrgInvite }) {
  const client = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => revokeInvite(orgId, invite.id),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.org(orgId) }),
  });
  return (
    <Row muted>
      <Avatar name={invite.name ?? null} email={invite.email} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate text-body text-chalk">
          <span className="truncate">{invite.name?.trim() || invite.email}</span>
          <Badge tone="caution">Invited</Badge>
        </p>
        <p className="truncate text-caption text-chalk-faint">
          {invite.name?.trim() ? `${invite.email} · ` : ''}sent {shortDate(invite.createdAt)} · expires {shortDate(invite.expiresAt)}
          {invite.seat === 'paid' ? ' · holds a paid seat' : ''}
        </p>
      </div>
      <span className="text-caption text-chalk-dim">
        {roleLabel(invite.role)} · {invite.seat === 'paid' ? 'Paid seat' : 'Free'}
      </span>
      {invite.acceptUrl ? (
        <CopyButton value={invite.acceptUrl} label="Copy link" copiedLabel="Copied" variant="ghost" size="sm" />
      ) : null}
      <button
        type="button"
        onClick={() => revoke.mutate()}
        disabled={revoke.isPending}
        aria-label={`Revoke the invitation for ${invite.email}`}
        title="Revoke"
        className="flex h-8 w-8 items-center justify-center rounded text-chalk-faint hover:bg-slate-800 hover:text-chalk"
      >
        <XIcon size={14} />
      </button>
      {revoke.isError ? (
        <div className="w-full">
          <ErrorText>{errorMessage(revoke.error)}</ErrorText>
        </div>
      ) : null}
    </Row>
  );
}

/**
 * The moment of purchase. Buying or releasing seats shows the exact
 * number from Stripe before anything changes: what is due today, what
 * the plan costs from here, and when. No separate checkout; the card on
 * file is charged the way every seat-based plan does it, but never
 * without this screen.
 */
export function SeatChangeDialog({
  orgId,
  seats,
  onDone,
  onClose,
}: {
  orgId: string;
  /** The count to change to. */
  seats: number;
  onDone: () => void;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const quote = useQuery({ queryKey: ['seat-quote', orgId, seats], queryFn: () => previewSeats(orgId, seats) });
  const change = useMutation({
    mutationFn: () => setSeatCount(orgId, seats),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.org(orgId) });
      void client.invalidateQueries({ queryKey: qk.orgs() });
      onDone();
    },
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q: SeatQuote | undefined = quote.data;
  const adding = q ? q.seats > q.current : true;
  const delta = q ? Math.abs(q.seats - q.current) : 1;
  const title = adding
    ? `Add ${delta === 1 ? 'a paid seat' : `${delta} paid seats`}`
    : `Remove ${delta === 1 ? 'a paid seat' : `${delta} paid seats`}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="seat-change-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-sm rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk">
          <XIcon size={16} />
        </button>
        <h2 id="seat-change-title" className="text-h4 text-chalk">
          {title}
        </h2>

        {quote.isPending ? <p className="mt-3 text-body text-chalk-dim">Getting the number from Stripe.</p> : null}
        {quote.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(quote.error)}</ErrorText>
          </div>
        ) : null}

        {q ? (
          <dl className="mt-4 divide-y divide-hairline rounded border border-hairline">
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">Paid seats</dt>
              <dd className="text-body text-chalk tnum">
                {q.current} <span className="text-chalk-faint">to</span> {q.seats}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">Due today</dt>
              <dd className="text-body text-chalk tnum">
                {q.billed ? dollars(q.dueNowCents) : '$0'}
                {q.billed && adding && q.dueNowCents > 0 ? (
                  <span className="block text-caption text-chalk-faint">prorated to {q.periodEnd ? shortDate(q.periodEnd) : 'the period end'}</span>
                ) : null}
                {q.billed && !adding ? <span className="block text-caption text-chalk-faint">credit applied to the next invoice</span> : null}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">{q.billed ? `From ${q.periodEnd ? shortDate(q.periodEnd) : 'next period'}` : 'After the trial'}</dt>
              <dd className="text-body text-chalk tnum">{dollars(q.monthlyCents)} a month</dd>
            </div>
          </dl>
        ) : null}

        {q && !q.billed ? (
          <p className="mt-3 text-caption text-chalk-faint">
            Nothing is charged during the trial{q.periodEnd ? `, which ends ${shortDate(q.periodEnd)}` : ''}. Set up billing before then to keep the seats.
          </p>
        ) : null}
        {change.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(change.error)}</ErrorText>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={change.isPending} disabled={!q} onClick={() => change.mutate()}>
            {q?.billed && adding && q.dueNowCents > 0 ? `Pay ${dollars(q.dueNowCents)} and add` : adding ? 'Add seat' : 'Remove seat'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Adding someone: a modal over the page, since it asks three things and
 * the answer to one (the seat) changes the bill.
 */
function AddUserDialog({
  orgId,
  seatPrice,
  seats,
  seatsUsed,
  isOwner,
  onClose,
}: {
  orgId: string;
  seatPrice: string | null;
  seats: number;
  seatsUsed: number;
  isOwner: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const spare = Math.max(0, seats - seatsUsed);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: qk.org(orgId) });
    void client.invalidateQueries({ queryKey: qk.orgs() });
  };
  // No spare seat: the owner is shown the purchase first; the invite goes
  // out once the seat is theirs. The form stays filled in behind it.
  const [buying, setBuying] = useState<{ email: string; name?: string; role: Role; seat: SeatLevel } | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [seat, setSeat] = useState<SeatLevel>('paid');
  const [invalid, setInvalid] = useState<string | null>(null);
  const [sent, setSent] = useState<{ email: string; acceptUrl: string } | null>(null);

  const invite = useMutation({
    mutationFn: (input: { email: string; name?: string; role: Role; seat: SeatLevel }) => inviteMember(orgId, input),
    onSuccess: (data, input) => {
      setSent({ email: input.email, acceptUrl: data.invite.acceptUrl });
      refresh();
    },
  });
  const needsSeat = (role === 'user' ? seat : 'paid') === 'paid' && spare === 0;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const effectiveSeat: SeatLevel = role === 'user' ? seat : 'paid';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-user-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-md rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk"
        >
          <XIcon size={16} />
        </button>
        <h2 id="add-user-title" className="text-h4 text-chalk">
          Add a user
        </h2>
        <p className="mt-1 text-caption text-chalk-dim">
          They get a link to join. A paid seat is theirs from the moment the invite goes out; a free one costs nothing.
        </p>

        {sent ? (
          <div className="mt-5 space-y-3">
            <p className="text-body text-chalk">
              <span className="text-go">Invitation ready for {sent.email}.</span> Send them this link; only
              they can accept it.
            </p>
            <p className="break-all rounded border border-hairline bg-slate-950 px-3 py-2 font-mono text-caption text-chalk-dim">
              {sent.acceptUrl}
            </p>
            <div className="flex justify-end gap-2">
              <CopyButton value={sent.acceptUrl} label="Copy link" copiedLabel="Copied" variant="secondary" size="sm" />
              <Button
                size="sm"
                onClick={() => {
                  setSent(null);
                  setName('');
                  setEmail('');
                }}
              >
                Add another
              </Button>
              <Button size="sm" variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="mt-5 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              setInvalid(null);
              const trimmed = email.trim();
              if (!trimmed.includes('@') || trimmed.length < 5) {
                setInvalid('That does not look like an email address.');
                return;
              }
              const input = { email: trimmed, name: name.trim() || undefined, role, seat: effectiveSeat };
              if (needsSeat && isOwner) setBuying(input);
              else invite.mutate(input);
            }}
          >
            <Input label="Name" placeholder="Sam Rivera" value={name} maxLength={80} autoFocus onChange={(event) => setName(event.target.value)} />
            <Input
              label="Email"
              type="email"
              placeholder="sam@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            <fieldset>
              <legend className="block text-caption font-medium text-chalk-dim">Seat</legend>
              <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                {SEATS.map((option) => {
                  const forced = role !== 'user';
                  const selected = effectiveSeat === option.value;
                  return (
                    <label
                      key={option.value}
                      className={cx(
                        'flex cursor-pointer items-start gap-2.5 rounded border p-3 transition-colors',
                        selected ? 'border-signal/60 bg-signal/10' : 'border-hairline hover:border-slate-600',
                        forced && !selected && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <input
                        type="radio"
                        name="seat"
                        value={option.value}
                        checked={selected}
                        disabled={forced}
                        onChange={() => setSeat(option.value)}
                        className="mt-0.5 accent-signal"
                      />
                      <span>
                        <span className="block text-body text-chalk">
                          {option.label}
                          {option.value === 'paid' && seatPrice ? (
                            <span className="text-chalk-faint"> · {seatPrice}/mo</span>
                          ) : null}
                        </span>
                        <span className="block text-caption text-chalk-faint">
                          {option.value === 'paid'
                            ? 'Unlimited recordings and transcripts, wherever they record.'
                            : 'On the team, can watch and comment; their own recordings stay on the free plan.'}
                        </span>
                        {option.value === 'paid' ? (
                          <span className={cx('mt-1 block text-caption', spare > 0 ? 'text-chalk-dim' : 'text-caution')}>
                            {spare > 0
                              ? `${spare} of ${seats} paid ${seats === 1 ? 'seat' : 'seats'} spare`
                              : `All ${seats} paid ${seats === 1 ? 'seat is' : 'seats are'} taken`}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <fieldset>
              <legend className="block text-caption font-medium text-chalk-dim">Role</legend>
              <div className="mt-1.5 overflow-hidden rounded border border-hairline">
                {ROLES.map((option) => {
                  const selected = role === option;
                  return (
                    <label
                      key={option}
                      className={cx(
                        'flex cursor-pointer items-start gap-2.5 border-b border-hairline p-3 transition-colors last:border-b-0',
                        selected ? 'bg-signal/10' : 'hover:bg-slate-800/60',
                      )}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={option}
                        checked={selected}
                        onChange={() => setRole(option)}
                        className="mt-0.5 accent-signal"
                      />
                      <span>
                        <span className="block text-body text-chalk">
                          {roleLabel(option)}
                          {option !== 'user' ? <span className="text-chalk-faint"> · paid seat</span> : null}
                        </span>
                        <span className="block text-caption text-chalk-faint">{roleDescription(option)}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {needsSeat && !isOwner ? (
              <p className="rounded border border-caution/40 bg-caution/10 px-3 py-2 text-caption text-chalk">
                Every paid seat is taken. Ask an owner to add one, or invite them on a free seat.
              </p>
            ) : null}
            {invalid ? <ErrorText>{invalid}</ErrorText> : null}
            {invite.isError ? (
              <ErrorText>
                {invite.error instanceof ApiError && (invite.error.details as { code?: string } | undefined)?.code === 'no_spare_seat'
                  ? 'Every paid seat is taken. Add a seat and try again.'
                  : errorMessage(invite.error)}
              </ErrorText>
            ) : null}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" busy={invite.isPending} disabled={needsSeat && !isOwner}>
                {needsSeat && isOwner ? 'Buy a seat and invite' : 'Send invite'}
              </Button>
            </div>
            {buying ? (
              <SeatChangeDialog
                orgId={orgId}
                seats={seats + 1}
                onDone={() => {
                  const input = buying;
                  setBuying(null);
                  invite.mutate(input);
                }}
                onClose={() => setBuying(null)}
              />
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}

/** Members and invitations in one list, seats bought and used in the header. */
export function TeamRoster({
  detail,
  viewerEmail,
  canManage = true,
  isOwner = false,
  seatPrice = null,
}: {
  detail: OrgDetail;
  viewerEmail: string | null;
  /** Admins and owners; a plain member sees the list without the controls. */
  canManage?: boolean;
  /** Owners buy and release seats. */
  isOwner?: boolean;
  /** "$7", for the dialog and the seat line. */
  seatPrice?: string | null;
}) {
  const [adding, setAdding] = useState(false);
  const seats = detail.seatCount;
  const used = detail.seatsUsed ?? detail.memberships.filter((m) => m.seat === 'paid').length;
  const free = detail.memberships.filter((m) => m.seat === 'free').length;
  const pending = detail.invites.length;

  const [changingTo, setChangingTo] = useState<number | null>(null);

  return (
    <section className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-h4 text-chalk">Users</h2>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-caption text-chalk-faint tnum">
            <span>
              {used} of {seats} paid {seats === 1 ? 'seat' : 'seats'} used
              {free > 0 ? ` · ${free} free` : ''}
              {pending > 0 ? ` · ${pending} invited` : ''}
            </span>
            {isOwner ? (
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setChangingTo(seats + 1)}
                  title={seatPrice ? `Add a paid seat, ${seatPrice} a month` : 'Add a paid seat'}
                  className="rounded px-1.5 py-0.5 text-signal hover:bg-slate-800 disabled:opacity-50"
                >
                  + Add seat
                </button>
                {seats > used ? (
                  <button
                    type="button"
                    onClick={() => setChangingTo(seats - 1)}
                    title="Stop paying for an unused seat"
                    className="rounded px-1.5 py-0.5 text-chalk-dim hover:bg-slate-800 hover:text-chalk disabled:opacity-50"
                  >
                    Remove seat
                  </button>
                ) : null}
              </span>
            ) : null}
          </p>
        </div>
        {canManage ? (
          <Button size="sm" onClick={() => setAdding(true)}>
            <PlusIcon size={13} />
            Add user
          </Button>
        ) : null}
      </div>
      <ul className="mt-3">
        {detail.memberships.map((member) => (
          <MemberRow
            key={member.user.id}
            orgId={detail.id}
            member={member}
            isSelf={!canManage || (viewerEmail !== null && member.user.email.toLowerCase() === viewerEmail)}
          />
        ))}
        {detail.invites.map((invite) => (
          <InviteRow key={invite.id} orgId={detail.id} invite={invite} />
        ))}
      </ul>
      {changingTo !== null ? (
        <SeatChangeDialog orgId={detail.id} seats={changingTo} onDone={() => setChangingTo(null)} onClose={() => setChangingTo(null)} />
      ) : null}
      {adding ? (
        <AddUserDialog
          orgId={detail.id}
          seatPrice={seatPrice}
          seats={seats}
          seatsUsed={used}
          isOwner={isOwner}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </section>
  );
}
